import { addMinutes, format, subMinutes } from 'date-fns';
import { parseBusinessTime } from '../../shared/booking/businessHours/parseBusinessTime';
import { slotFitsEmployeeSchedule } from '../../shared/booking/employee-schedule/workingSchedule';
import {
  isAnyEmployeeSlotAvailable,
  isEmployeeSlotAvailable,
} from '../../shared/booking/double-booking/employeeSlotAvailability';

const DEBUG_STORAGE_KEY = 'storefrontBookingDebug';
const TRACE_BUFFER_KEY = '__STOREFRONT_BOOKING_TRACE__';

const getTraceBuffer = () => {
  if (typeof window === 'undefined') return [];
  if (!window[TRACE_BUFFER_KEY]) {
    window[TRACE_BUFFER_KEY] = [];
  }
  return window[TRACE_BUFFER_KEY];
};

export const installStorefrontBookingTraceHelpers = () => {
  if (typeof window === 'undefined') return;

  if (!window.dumpStorefrontBookingTrace) {
    window.dumpStorefrontBookingTrace = () => {
      const rows = getTraceBuffer();
      console.log(`[StorefrontBooking] trace dump (${rows.length} entries)`);
      rows.forEach((entry, index) => {
        console.log(`#${index + 1} ${entry.stage}`, entry.payload);
      });
      return rows;
    };
  }

  if (!window.clearStorefrontBookingTrace) {
    window.clearStorefrontBookingTrace = () => {
      if (window[TRACE_BUFFER_KEY]) {
        window[TRACE_BUFFER_KEY].length = 0;
      }
    };
  }
};

installStorefrontBookingTraceHelpers();

/** Trace (console + in-memory buffer). Off by default — enable with ?bookingDebug=1 or localStorage storefrontBookingDebug=1 */
export const traceStorefrontBooking = (stage, payload = {}) => {
  if (!isStorefrontBookingDebugEnabled()) return;

  const entry = {
    ts: new Date().toISOString(),
    stage,
    payload,
  };

  const buffer = getTraceBuffer();
  buffer.push(entry);
  if (buffer.length > 250) {
    buffer.splice(0, buffer.length - 250);
  }

  console.log(`[StorefrontBooking] ${stage}`, payload);
};

export const isStorefrontBookingDebugEnabled = () => {
  if (typeof window === 'undefined') return false;

  try {
    if (window.localStorage?.getItem(DEBUG_STORAGE_KEY) === '1') {
      return true;
    }
  } catch (_) {
    // Ignore storage access errors.
  }

  try {
    return new URLSearchParams(window.location.search).get('bookingDebug') === '1';
  } catch (_) {
    return false;
  }
};

export const logStorefrontBooking = (stage, payload = {}) => {
  if (!isStorefrontBookingDebugEnabled()) return;
  console.info(`[StorefrontBooking:verbose:${stage}]`, payload);
};

export const warnStorefrontBooking = (message, payload = {}) => {
  if (!isStorefrontBookingDebugEnabled()) return;
  traceStorefrontBooking(message, payload);
  console.warn(`[StorefrontBooking] ${message}`, payload);
};

export const explainStorefrontStaffAssignment = ({
  locationId,
  serviceData,
  explicitEmployeeIds,
  eligibleEmployeeIds,
  companyStaff,
  staff,
}) => {
  const serviceLocationIds = Array.isArray(serviceData?.locationIds) ? serviceData.locationIds : [];
  const companyStaffIds = (companyStaff || []).map((member) => member.id);

  if (!explicitEmployeeIds?.length) {
    return 'Service has no employees assigned (employeeIds is empty).';
  }

  if (locationId && serviceLocationIds.length > 0 && !serviceLocationIds.includes(locationId)) {
    return `Service is not offered at location ${locationId}. Service locationIds: ${serviceLocationIds.join(', ')}`;
  }

  if (locationId && eligibleEmployeeIds?.length === 0) {
    return `Assigned employees are not tagged for location ${locationId}. Assigned: ${explicitEmployeeIds.join(', ')}. Staff at location: ${companyStaffIds.join(', ') || '(none)'}`;
  }

  if (staff?.length === 0 && eligibleEmployeeIds?.length > 0) {
    return `Eligible employee IDs could not be resolved to staff records: ${eligibleEmployeeIds.join(', ')}`;
  }

  return null;
};

export const generateStorefrontTimeSlots = ({
  date,
  companyHours,
  totalDuration,
  rosterForSlots,
  appointmentsForDay,
  bookingConflictSettings,
  staff,
}) => {
  const dayOfWeek = format(date, 'EEEE').toLowerCase();
  const diagnostics = {
    dayOfWeek,
    date: format(date, 'yyyy-MM-dd'),
    totalDurationMinutes: totalDuration,
    rosterCount: rosterForSlots?.length || 0,
    rosterIds: (rosterForSlots || []).map((member) => member.id),
    appointmentCount: appointmentsForDay?.length || 0,
    isOpen: Boolean(companyHours?.[dayOfWeek]?.isOpen),
    open: companyHours?.[dayOfWeek]?.open || null,
    close: companyHours?.[dayOfWeek]?.close || null,
    candidateSlots: 0,
    rejectedPast: 0,
    rejectedNoSchedule: 0,
    rejectedConflict: 0,
    accepted: 0,
    blockReason: null,
  };

  if (!companyHours || !companyHours[dayOfWeek] || !companyHours[dayOfWeek].isOpen) {
    diagnostics.blockReason = 'BUSINESS_CLOSED';
    return { slots: [], diagnostics };
  }

  const dayHours = companyHours[dayOfWeek];
  const openTime = parseBusinessTime(dayHours.open, date);
  const closeTime = parseBusinessTime(dayHours.close, date);

  if (!openTime || !closeTime || !(closeTime > openTime)) {
    diagnostics.blockReason = 'INVALID_BUSINESS_HOURS';
    diagnostics.parsedOpen = openTime ? openTime.toISOString() : null;
    diagnostics.parsedClose = closeTime ? closeTime.toISOString() : null;
    return { slots: [], diagnostics };
  }

  diagnostics.parsedOpen = openTime.toISOString();
  diagnostics.parsedClose = closeTime.toISOString();

  const slots = [];
  let currentTime = new Date(date);
  currentTime.setHours(openTime.getHours(), openTime.getMinutes(), 0, 0);

  const lastSlot = subMinutes(closeTime, totalDuration);
  const useAnyMode = !staff || staff.isAnyAvailable;
  const now = new Date();

  while (currentTime <= lastSlot) {
    diagnostics.candidateSlots += 1;
    const slotStart = new Date(currentTime);
    const slotEnd = addMinutes(slotStart, totalDuration);

    const scheduleQualifiedIds = (rosterForSlots || [])
      .filter((member) =>
        slotFitsEmployeeSchedule({
          workingSchedule: member.workingSchedule,
          date,
          slotStart,
          slotEnd,
          companyHours,
        })
      )
      .map((member) => member.id);

    const isScheduleMatch = useAnyMode
      ? scheduleQualifiedIds.length > 0
      : scheduleQualifiedIds.includes(staff?.id);

    const hasConflictFreeStaff = isScheduleMatch
      ? (useAnyMode
        ? isAnyEmployeeSlotAvailable({
          appointments: appointmentsForDay,
          employeeIds: scheduleQualifiedIds,
          slotStart,
          slotEnd,
          ...bookingConflictSettings,
        })
        : isEmployeeSlotAvailable({
          appointments: appointmentsForDay,
          employeeId: staff?.id,
          slotStart,
          slotEnd,
          ...bookingConflictSettings,
        }))
      : false;

    if (slotStart <= now) {
      diagnostics.rejectedPast += 1;
    } else if (!isScheduleMatch) {
      diagnostics.rejectedNoSchedule += 1;
    } else if (!hasConflictFreeStaff) {
      diagnostics.rejectedConflict += 1;
    } else {
      diagnostics.accepted += 1;
      slots.push(new Date(currentTime));
    }

    currentTime = addMinutes(currentTime, 15);
  }

  if (slots.length === 0) {
    if (diagnostics.candidateSlots === 0) {
      diagnostics.blockReason = 'DURATION_TOO_LONG_FOR_HOURS';
    } else if (diagnostics.rejectedNoSchedule === diagnostics.candidateSlots) {
      diagnostics.blockReason = 'NO_EMPLOYEE_SCHEDULE_MATCH';
    } else if (diagnostics.rejectedPast === diagnostics.candidateSlots) {
      diagnostics.blockReason = 'ALL_SLOTS_IN_PAST';
    } else if (diagnostics.rejectedConflict > 0 && diagnostics.rejectedNoSchedule > 0) {
      diagnostics.blockReason = 'SCHEDULE_AND_CONFLICTS';
    } else if (diagnostics.rejectedConflict > 0) {
      diagnostics.blockReason = 'ALL_SLOTS_CONFLICT';
    } else {
      diagnostics.blockReason = 'UNKNOWN';
    }
  }

  return { slots, diagnostics };
};

export const buildAvailableTimeSlots = (params) => generateStorefrontTimeSlots(params).slots;

export const summarizeSlotDiagnostics = (diagnostics = {}) => {
  const labels = {
    BUSINESS_CLOSED: 'Business is closed on this day.',
    INVALID_BUSINESS_HOURS: 'Business hours could not be parsed for this day.',
    DURATION_TOO_LONG_FOR_HOURS: 'Service duration is longer than the open hours window.',
    NO_EMPLOYEE_SCHEDULE_MATCH: 'No assigned staff are scheduled to work during business hours.',
    ALL_SLOTS_IN_PAST: 'All candidate times are in the past.',
    ALL_SLOTS_CONFLICT: 'All candidate times conflict with existing appointments.',
    SCHEDULE_AND_CONFLICTS: 'Times are blocked by staff schedules and/or existing appointments.',
    UNKNOWN: 'No slots matched the availability rules.',
  };

  return {
    ...diagnostics,
    summary: labels[diagnostics.blockReason] || labels.UNKNOWN,
    enableVerboseLogs: "localStorage.setItem('storefrontBookingDebug','1') then refresh, or add ?bookingDebug=1",
    viewTrace: 'Run dumpStorefrontBookingTrace() in the console.',
  };
};
